/**
 * Where a lab result sits on the range bar: one rule for every surface.
 *
 * Home result cards, All results, a cycle's tests, the AMH and test-panel
 * pages, the result dialog and the documents list all ask this module, so a
 * result sits in the same place wherever she meets it.
 *
 * Priority:
 *   1. The lab's own assessment (`TestResult.clinicalAssessment`), kept as
 *      the lab gave it: low, normal or high. "unknown" is not an assessment.
 *   2. Otherwise the client thresholds below, when the test is one they cover
 *      and the timing they need is known.
 *   3. Otherwise no category. The bar shows her value without a position.
 *
 * A trend (this value against the previous one) is never a category: a value
 * that rose since last time can still be perfectly normal.
 *
 * ------------------------------------------------------------------------
 * SOURCES
 * ------------------------------------------------------------------------
 * Every client threshold is taken from a guideline or a peer-reviewed paper,
 * cited next to the value. Change a value only for a newer source, and cite
 * it. Where no guideline gives a cut-off (LH; FSH outside cycle days 2 to 4;
 * estradiol during stimulation below the OHSS level; progesterone outside the
 * windows below) there is no client category, and a value with a unit the
 * thresholds can't read is not placed either. The lab's own assessment, when
 * it gives one, still places the value. The "Typical range" copy in
 * `content/biomarker-factsheets.ts` says the same thing in words.
 *
 *   - ASRM Practice Committee 2020, Testing and interpreting measures of
 *     ovarian reserve (Fertil Steril 114:1151): AMH, basal FSH, basal
 *     estradiol.
 *     https://www.asrm.org/practice-guidance/practice-committee-documents/testing-and-interpreting-measures-of-ovarian-reserve-a-committee-opinion-2020/
 *   - ASRM Practice Committee 2023, Prevention of moderate and severe ovarian
 *     hyperstimulation syndrome: estradiol at trigger, AMH.
 *     https://www.asrm.org/practice-guidance/practice-committee-documents/prevention-of-moderate-and-severe-ovarian-hyperstimulation-syndrome-a-guideline-2023/
 *   - Venetis et al. 2013 (Hum Reprod Update 19:433), Bosch et al. 2010
 *     (Hum Reprod 25:2092), Hamdine et al. 2014 (Fertil Steril): progesterone
 *     during stimulation and before it starts.
 *   - Labarta et al. 2021 (Hum Reprod 36:683), Melo et al. 2021 (Fertil Steril
 *     116:1534): progesterone around a programmed frozen transfer.
 *   - ASRM Practice Committee 2015, Diagnostic evaluation of the infertile
 *     female: a luteal progesterone of 3 ng/mL or more is presumptive evidence
 *     of ovulation. ASRM 2021 (luteal phase deficiency): no serum level
 *     diagnoses a luteal phase defect, so none is set above it.
 *   - WHO laboratory manual for the examination and processing of human semen,
 *     6th edition (2021): semen 5th centiles.
 *     https://www.who.int/publications/i/item/9789240030787
 */

import { getCanonicalResultValue } from "./canonical-results";
import type { TestResult } from "./hooks/useTestResults";
import {
  resolveResultContextFromCycles,
  type ResultCycleSources,
  type ResultInterpretationContext,
} from "./interpretation-context";

export type ResultCategory = "normal" | "low" | "high" | "borderline";

/**
 * Where a category came from. "api" is the lab's own assessment; "client" is
 * the published-guideline thresholds below.
 */
export type ResultCategorySource = "api" | "client";

export type ResolvedResultCategory = {
  category: ResultCategory;
  source: ResultCategorySource;
};

/** The lab's assessment exactly as the API states it. */
export type LabAssessment = TestResult["clinicalAssessment"];

/** The tests the client thresholds cover, as identifiers rather than copy. */
export type InterpretedTest =
  "amh" | "fsh" | "progesterone" | "e2" | "lh" | "semen_analysis";

/** The timing a cycle-dependent threshold needs. */
export type ResultCategoryContext = Pick<
  ResultInterpretationContext,
  "cycleDay" | "treatmentPhase" | "afterTransfer"
>;

export type ResultCategoryInput = {
  name: string | null | undefined;
  value: string | number | null | undefined;
  unit?: string | null;
  /** The lab's own assessment. Wins whenever it is low, normal or high. */
  clinicalAssessment?: LabAssessment;
  /** Cycle day and treatment phase, when known. */
  context?: ResultCategoryContext | null;
};

/**
 * The fields of a result that decide its category, shared by the raw API row
 * and the canonical result so every surface hands over the same input.
 */
export type CategorizableResult = {
  name: string | null;
  value: string;
  unit: string | null;
  clinicalAssessment: LabAssessment;
  date: string | null;
  cycleId: string | null;
  phaseId: string | null;
};

/** A raw API row in the shape `CategorizableResult` expects. */
export function categorizableFromTestResult(
  result: TestResult,
): CategorizableResult {
  return {
    name: result.name,
    value: getCanonicalResultValue(result),
    unit: result.unit,
    clinicalAssessment: result.clinicalAssessment,
    date: result.date,
    cycleId: result.cycleId,
    phaseId: result.phaseId,
  };
}

/**
 * The lab's assessment as a category. Null when the lab has not assessed the
 * row ("unknown" or absent), so the caller can fall back to the thresholds.
 */
export function categoryFromLabAssessment(
  assessment: LabAssessment | undefined,
): ResultCategory | null {
  if (assessment === "low") return "low";
  if (assessment === "normal") return "normal";
  if (assessment === "high") return "high";
  return null;
}

/** Which threshold set a result's name selects, if any. */
export function getInterpretedTest(
  name: string | null | undefined,
): Exclude<InterpretedTest, "semen_analysis"> | null {
  const normalized = (name ?? "").toLowerCase();
  if (normalized.includes("amh") || normalized.includes("anti-mullerian")) {
    return "amh";
  }
  if (
    normalized.includes("fsh") ||
    normalized.includes("follicle stimulating") ||
    normalized.includes("follicle-stimulating")
  ) {
    return "fsh";
  }
  if (normalized.includes("progesterone")) return "progesterone";
  if (
    normalized.includes("estradiol") ||
    normalized.includes("oestradiol") ||
    /\be2\b/.test(normalized)
  ) {
    return "e2";
  }
  if (
    /\blh\b/.test(normalized) ||
    normalized.includes("luteinizing") ||
    normalized.includes("luteinising")
  ) {
    return "lh";
  }
  return null;
}

// -------------------------------------------------------------
// Units
// -------------------------------------------------------------

/**
 * The value in the unit the thresholds use (ng/mL for AMH and progesterone,
 * pg/mL for estradiol), or null when the unit is missing or not one of these.
 * Each is reported in two units a few times apart, so a bare number can't be
 * placed. µg/L equals ng/mL and ng/L equals pg/mL.
 */
function toThresholdUnit(
  test: "amh" | "e2" | "progesterone",
  value: number,
  unit: string,
): number | null {
  const u = unit.trim().toLowerCase().replace("\u03bc", "\u00b5");
  if (!u) return null;
  const microgramsPerLitre = /^[\u00b5u]g\/l$/.test(u);
  switch (test) {
    case "amh":
      // 1 ng/mL = 7.14 pmol/L
      if (u.includes("pmol")) return value / 7.14;
      return u.includes("ng/ml") || microgramsPerLitre ? value : null;
    case "e2":
      // 1 pg/mL = 3.67 pmol/L
      if (u.includes("pmol")) return value / 3.67;
      return u.includes("pg/ml") || u === "ng/l" ? value : null;
    case "progesterone":
      // 1 ng/mL = 3.18 nmol/L
      if (u.includes("nmol")) return value / 3.18;
      return u.includes("ng/ml") || microgramsPerLitre ? value : null;
  }
}

// -------------------------------------------------------------
// Client thresholds: value to category, or null when no guideline
// covers the timing (issue #1593 point 4: don't invent defaults).
// -------------------------------------------------------------

const isBasalDay = (cycleDay: number | null): cycleDay is number =>
  cycleDay !== null && cycleDay >= 2 && cycleDay <= 4;

/**
 * AMH, ng/mL, any cycle day. ASRM 2020: below 1.0 is low, 1.0 to 3.5 is the
 * normal range. Above 3.5 is high: ASRM 2023 counts AMH above 3.4 among the
 * risk factors for OHSS.
 */
function amhCategorize(ngPerMl: number): ResultCategory {
  if (ngPerMl < 1) return "low";
  if (ngPerMl <= 3.5) return "normal";
  return "high";
}

/**
 * FSH, IU/L, on cycle day 2, 3 or 4 only. ASRM 2020: above 10 IU/L is high
 * (specific, not sensitive, for diminished ovarian reserve). FSH on other
 * days says nothing about ovarian reserve, so it gets no category.
 */
function fshCategorize(
  value: number,
  cycleDay: number | null,
): ResultCategory | null {
  if (!isBasalDay(cycleDay)) return null;
  return value > 10 ? "high" : "normal";
}

/**
 * Estradiol, pg/mL.
 * - Cycle day 2 to 4: ASRM 2020 calls a basal value above 60 to 80 pg/mL
 *   elevated; it can also mask a high FSH.
 * - During stimulation: ASRM 2023 counts above 3,500 pg/mL at trigger among
 *   the risk factors for OHSS. Lower values depend on the stimulation day and
 *   the follicle count, which no guideline turns into a range.
 */
function e2Categorize(
  pgPerMl: number,
  phase: string | null,
  cycleDay: number | null,
): ResultCategory | null {
  if (phase === "protocol") return pgPerMl > 3500 ? "high" : null;
  if (!isBasalDay(cycleDay)) return null;
  if (pgPerMl <= 60) return "normal";
  if (pgPerMl <= 80) return "borderline";
  return "high";
}

/**
 * Progesterone, ng/mL.
 * - Around a transfer: Labarta 2021 found lower ongoing pregnancy below
 *   8.8 ng/mL on transfer day (programmed cycles, vaginal progesterone);
 *   Melo 2021's meta-analysis found outcomes best at 10 ng/mL or more.
 * - The luteal phase after an insemination or ovulation: 3 ng/mL or more is
 *   presumptive evidence of ovulation (ASRM 2015). ASRM 2021 sets no higher
 *   bar, since no serum level diagnoses a luteal phase defect.
 * - During stimulation, and on cycle day 2 to 4 before it starts: Venetis
 *   2013 found pregnancy less likely from 0.8 ng/mL; 1.5 ng/mL is the usual
 *   cut-off for an elevation (Bosch 2010; Hamdine 2014 before stimulation).
 * Otherwise (a day-21 check outside treatment, the pill, other phases) no
 * guideline places it, so it gets no category.
 */
function progesteroneCategorize(
  ngPerMl: number,
  context: ResultCategoryContext | null | undefined,
): ResultCategory | null {
  const phase = context?.treatmentPhase ?? null;
  if (
    phase === "transfer" ||
    (phase === "luteal_phase" && context?.afterTransfer === true)
  ) {
    if (ngPerMl < 8.8) return "low";
    if (ngPerMl < 10) return "borderline";
    return "normal";
  }
  if (phase === "luteal_phase") return ngPerMl >= 3 ? "normal" : "low";
  if (phase === "protocol" || isBasalDay(context?.cycleDay ?? null)) {
    if (ngPerMl < 0.8) return "normal";
    if (ngPerMl < 1.5) return "borderline";
    return "high";
  }
  return null;
}

/**
 * Semen analysis: the WHO 6th edition (2021) 5th centiles of fertile men:
 * concentration 16 million/mL, total motility 42%, normal forms 4%, volume
 * 1.4 mL. WHO calls them reference values, not a diagnosis, so one value
 * under its centile reads as borderline and two or more as low.
 */
function saCategorize(results: SemenAnalysisParameters): ResultCategory {
  let issues = 0;
  if (results.count !== undefined && results.count < 16) issues++;
  if (results.motility !== undefined && results.motility < 42) issues++;
  if (results.morphology !== undefined && results.morphology < 4) issues++;
  if (results.volume !== undefined && results.volume < 1.4) issues++;
  if (issues === 0) return "normal";
  if (issues === 1) return "borderline";
  return "low";
}

function clientCategory(
  test: Exclude<InterpretedTest, "semen_analysis">,
  value: number,
  unit: string,
  context: ResultCategoryContext | null | undefined,
): ResultCategory | null {
  const cycleDay = context?.cycleDay ?? null;
  const treatmentPhase = context?.treatmentPhase ?? null;

  switch (test) {
    case "amh": {
      const ngPerMl = toThresholdUnit("amh", value, unit);
      return ngPerMl === null ? null : amhCategorize(ngPerMl);
    }
    case "fsh":
      return fshCategorize(value, cycleDay);
    case "progesterone": {
      const ngPerMl = toThresholdUnit("progesterone", value, unit);
      return ngPerMl === null ? null : progesteroneCategorize(ngPerMl, context);
    }
    case "e2": {
      const pgPerMl = toThresholdUnit("e2", value, unit);
      return pgPerMl === null
        ? null
        : e2Categorize(pgPerMl, treatmentPhase, cycleDay);
    }
    case "lh":
      // No ASRM or ESHRE cut-off places an LH value by itself.
      return null;
  }
}

/**
 * The category for one result: the lab's assessment, otherwise the client
 * thresholds, otherwise null (no category). Never derived from a trend.
 */
export function resolveResultCategory(
  input: ResultCategoryInput,
): ResolvedResultCategory | null {
  const fromLab = categoryFromLabAssessment(input.clinicalAssessment);
  if (fromLab) return { category: fromLab, source: "api" };

  const test = getInterpretedTest(input.name);
  if (!test) return null;
  const numericValue = Number.parseFloat(String(input.value ?? ""));
  if (Number.isNaN(numericValue)) return null;

  const category = clientCategory(
    test,
    numericValue,
    input.unit ?? "",
    input.context,
  );
  return category ? { category, source: "client" } : null;
}

export type ResultCategoryResolver = {
  /** The timing context the category rule uses for this result. */
  contextFor: (
    result: Pick<CategorizableResult, "name" | "date" | "cycleId" | "phaseId">,
  ) => ResultInterpretationContext;
  /**
   * The result's category, or null when neither the lab nor the thresholds
   * can place it. Pass `context` only when the surface is scoped to one cycle
   * and knows that cycle's phases better than the active-cycle lookup.
   */
  categorize: (
    result: CategorizableResult,
    context?: ResultInterpretationContext,
  ) => ResolvedResultCategory | null;
};

/**
 * The rule bound to the input every surface must share: the timing of each
 * result, read from her cycles. `useResultCategoryResolver`
 * builds this from live data; every results surface categorizes through it.
 */
export function createResultCategoryResolver({
  sources,
}: {
  sources: ResultCycleSources;
}): ResultCategoryResolver {
  const contextFor: ResultCategoryResolver["contextFor"] = (result) =>
    resolveResultContextFromCycles(result, sources);
  return {
    contextFor,
    categorize: (result, context) =>
      resolveResultCategory({
        name: result.name,
        value: result.value,
        unit: result.unit,
        clinicalAssessment: result.clinicalAssessment,
        context: context ?? contextFor(result),
      }),
  };
}

export type SemenAnalysisParameters = {
  count?: number;
  motility?: number;
  morphology?: number;
  volume?: number;
};

/**
 * The aggregate semen-analysis category: the lab's assessment, otherwise the
 * WHO lower reference limits over whichever parameters were measured.
 */
export function resolveSemenAnalysisCategory(input: {
  clinicalAssessment?: LabAssessment;
  parameters: SemenAnalysisParameters;
}): ResolvedResultCategory {
  const fromLab = categoryFromLabAssessment(input.clinicalAssessment);
  if (fromLab) return { category: fromLab, source: "api" };
  return { category: saCategorize(input.parameters), source: "client" };
}
